/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
'use memo';
import { SmartLink as Link } from '@/utils/smartLink';
import cx from 'classnames';
import { type YextStoreEntity } from '../../interface/yext-types';
import { useStoreDayInfo } from '../../hooks/useStoreDayInfo';
import styles from './StoreCard.module.scss';
import { Fragment, useState, use } from 'react';
import { View, Text, Button } from '@az/starc-ui';
import { formatPhoneNumber } from '@/utils/formatPhoneNumber';
import { useLabels } from '@/hooks/useLabels';
import { useLocale } from '@/hooks/useLocale';
import { getStoreLocationUrl } from '../../utils/getStoreLocationMapUrl';
import { FlexGrid } from '@/components/FlexGrid/FlexGrid';
import { useSetStore } from '@/features/fulfillment/hooks/useSetStore';
import dynamic from 'next/dynamic';
import { SnackBarContext } from '@/components/SnackBarContext';
import NextImage from '@/components/NextImage';
import { getPathFromWebsiteURL } from '../../utils/getPathFromWebsiteURL';
import { StoreCardDirectionsLinks } from './components/StoreCardDirectionsLinks/StoreCardDirectionsLinks';
import { type BottomSheetVariant } from '../../interface/types';
import starcClassOverrides from '@/theme/starcClassOverrides.module.scss';
import { useStoreClosedUntilText } from '../../hooks/useStoreClosedUntilText';

const LazySearchStoreModal = dynamic(() =>
  import('@/components/Store/StoreModal/SearchStoreModal').then((module) => ({
    default: module.SearchStoreModal,
  })),
);

type StoreCardProps = {
  store: YextStoreEntity;
  showActions?: boolean;
  isStoreDetailsCard?: boolean;
  openBottomSheet?: (variant: BottomSheetVariant) => void;
};
const labelMap = {
  getDirections: 'label_locations_directionsCTA',
  setStore: 'label_locations_StoreCard_setStore',
  storeLocator: 'label_locations_StoreCard_storeLocator',
  storeNumber: 'label_locations_StoreCard_storeNumber',
  lblYourStoreHasBeenUpdatedTo: 'label_your_store_has_been_updated_to',
};

export function StoreCard({
  store,
  isStoreDetailsCard,
  showActions = true,
  openBottomSheet,
}: StoreCardProps) {
  const { isOpen, statusText, hoursText } = useStoreDayInfo({
    hours: store.hours,
    closed: store.closed,
    timezone: store.timezone,
  });

  const closedUntilText = useStoreClosedUntilText(store);

  const [showStoreLocator, setShowStoreLocator] = useState(false);

  const labels = useLabels(labelMap);
  const locale = useLocale();

  const storeUrl = store.websiteUrl?.url ? getPathFromWebsiteURL(store.websiteUrl.url) : null;

  const directionsUrl = store.googlePlaceId
    ? getStoreLocationUrl({
        name: store.name,
        googlePlaceId: store.googlePlaceId,
      })
    : null;

  const { setStore, isPending } = useSetStore(() => {});

  const { showSnackBar: showSnackBarContext } = use(SnackBarContext);

  const handleSetStore = async () => {
    if (!store.address) {
      return;
    }
    await setStore(store.meta.id, store.address.postalCode, false);
    showSnackBarContext(`${labels.lblYourStoreHasBeenUpdatedTo} ${store.address.line1}`);
  };

  const handleOpenStoreLocator = () => {
    setShowStoreLocator(true);
  };

  const handleCloseStoreLocator = () => {
    setShowStoreLocator(false);
  };

  return (
    <FlexGrid
      flexDirection="column"
      className={styles.storeCard}
      gap={isStoreDetailsCard ? { sm: 4, md: 9 } : 4}
    >
      <div>
        <FlexGrid justifyContent="space-between" className={styles.storeCardHeaderContainer}>
          <div className={styles.storeCardHeader}>
            <div className={styles.storeName}>
              {storeUrl && !isStoreDetailsCard ? (
                <Link to={storeUrl}>
                  <Text
                    variant="large-body"
                    weight="bold"
                    className={starcClassOverrides['starc-large-body']}
                  >
                    {store.name}
                  </Text>
                </Link>
              ) : (
                <Text
                  variant="large-body"
                  weight="bold"
                  className={starcClassOverrides['starc-large-body']}
                >
                  {store.name}
                </Text>
              )}
            </div>
          </div>
          <View className={styles.storeNumberContainer}>
            <Text
              variant="small-body"
              className={cx(styles.storeNumber, starcClassOverrides['starc-small-body'])}
            >{`${labels.storeNumber}${store.meta.id}`}</Text>
          </View>
        </FlexGrid>
      </div>

      <FlexGrid flexDirection="column" gap={4}>
        <Text
          variant="large-body"
          className={cx(styles.storeStatus, starcClassOverrides['starc-large-body'])}
        >
          {closedUntilText ? (
            <span className={cx(styles.statusBadge, styles.closed)}>{closedUntilText}</span>
          ) : statusText || hoursText ? (
            <Fragment>
              <span className={cx(styles.statusBadge, isOpen ? styles.open : styles.closed)}>
                {statusText} {hoursText}
              </span>
            </Fragment>
          ) : (
            <View height="20px" />
          )}
        </Text>
        {store.address ? (
          <Text variant="main-body" className={starcClassOverrides['starc-main-body']}>
            <p>{store.address.line1}</p>
            <p>
              {store.address.city}, {store.address.region} {store.address.postalCode}
            </p>
          </Text>
        ) : null}
      </FlexGrid>

      {showActions ? (
        <>
          <StoreCardDirectionsLinks
            isStoreDetailsCard={isStoreDetailsCard}
            storeDetails={store}
            openBottomSheet={openBottomSheet}
          />
          <FlexGrid gap={2} flexDirection="row" className={styles.buttonContainer}>
            <Button
              className={styles.storeAction}
              onClick={handleSetStore}
              loading={isPending}
              size="small"
            >
              {labels.setStore}
            </Button>
            <Button
              className={styles.storeAction}
              variant="secondary"
              onClick={handleOpenStoreLocator}
              size="small"
            >
              {labels.storeLocator}
            </Button>
          </FlexGrid>
        </>
      ) : (
        <>
          {store.mainPhone && (
            <Text
              variant="inline-link"
              className={cx(styles.storePhoneNumber, starcClassOverrides['starc-inline-link'])}
              weight="medium"
              decoration="underline"
            >
              <Link to={store.mainPhone} isPhoneNumber>
                {formatPhoneNumber(store.mainPhone, locale, { stripCountryCode: true })}
              </Link>
            </Text>
          )}

          {directionsUrl && (
            <Link
              to={directionsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.directions}
            >
              {labels.getDirections}

              <NextImage src="/images/arrow-right-lightOrange.svg" alt="" width={18} height={10} />
            </Link>
          )}
        </>
      )}

      {showStoreLocator && (
        <LazySearchStoreModal
          searchValue={store.address?.postalCode}
          closeStoreModal={handleCloseStoreLocator}
          redirectToStore
        />
      )}
    </FlexGrid>
  );
}
